import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  type Updater,
  useReactTable,
} from "@tanstack/react-table";
import { DataTableEmpty } from "components/ui/Table/DataTableEmpty";
import { DataTablePagination } from "components/ui/Table/DataTablePagination";
import { DataTableSkeleton } from "components/ui/Table/DataTableSkeleton";
import { Table } from "components/ui/Table/Table";
import { type ReactNode, useState } from "react";

type PaginationConfig = {
  pageSize?: number;
  pageSizeOptions?: number[];
  showSizeChanger?: boolean;
  showTotal?: boolean;
};

export type DataTableProps<T> = {
  columns: ColumnDef<T, any>[];
  data: T[];

  /** 列ヘッダクリックでソート */
  sortable?: boolean;

  /** ページネーション */
  pagination?: boolean | PaginationConfig;

  /** 読み込み中 skeleton */
  isLoading?: boolean;

  /** 0 件時の表示 */
  emptyState?: ReactNode;

  /** 密度 */
  density?: "compact" | "normal" | "comfortable";
  zebra?: boolean;
  bordered?: boolean;
  stickyHeader?: boolean;

  /** 行クリック */
  onRowClick?: (row: T) => void;

  /** Controlled (サーバー側処理) 用 */
  sortState?: SortingState;
  onSortChange?: (state: SortingState) => void;
  paginationState?: PaginationState;
  onPaginationChange?: (state: PaginationState) => void;
  totalCount?: number;
  manualSorting?: boolean;
  manualPagination?: boolean;
};

const defaultPaginationConfig: Required<PaginationConfig> = {
  pageSize: 20,
  pageSizeOptions: [10, 20, 50, 100],
  showSizeChanger: true,
  showTotal: true,
};

/**
 * 全部入り DataTable。TanStack Table をエンジンにし、
 * sort / pagination / loading / empty を props で切り替える。
 * @param {DataTableProps} props DataTable props
 * @return {JSX.Element} テーブル UI
 */
export const DataTable = <T,>({
  columns,
  data,
  sortable = false,
  pagination = false,
  isLoading = false,
  emptyState,
  density = "compact",
  zebra = true,
  bordered,
  stickyHeader = true,
  onRowClick,
  sortState,
  onSortChange,
  paginationState,
  onPaginationChange,
  totalCount,
  manualSorting = false,
  manualPagination = false,
}: DataTableProps<T>): JSX.Element => {
  const paginationConfig: Required<PaginationConfig> = {
    ...defaultPaginationConfig,
    ...(typeof pagination === "object" ? pagination : {}),
  };

  const [uncontrolledSorting, setUncontrolledSorting] = useState<SortingState>(
    [],
  );
  const [uncontrolledPagination, setUncontrolledPagination] =
    useState<PaginationState>({
      pageIndex: 0,
      pageSize: paginationConfig.pageSize,
    });

  const sorting = sortState ?? uncontrolledSorting;
  const paginationStateValue = paginationState ?? uncontrolledPagination;

  const handleSortingChange = (updater: Updater<SortingState>): void => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    if (onSortChange) {
      onSortChange(next);
    } else {
      setUncontrolledSorting(next);
    }
  };

  const handlePaginationChange = (updater: Updater<PaginationState>): void => {
    const next =
      typeof updater === "function" ? updater(paginationStateValue) : updater;
    if (onPaginationChange) {
      onPaginationChange(next);
    } else {
      setUncontrolledPagination(next);
    }
  };

  const table = useReactTable<T>({
    data,
    columns,
    state: {
      sorting,
      pagination: paginationStateValue,
    },
    onSortingChange: handleSortingChange,
    onPaginationChange: handlePaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: sortable ? getSortedRowModel() : undefined,
    getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
    enableSorting: sortable,
    manualSorting,
    manualPagination,
    pageCount:
      manualPagination && totalCount !== undefined
        ? Math.ceil(totalCount / paginationStateValue.pageSize)
        : undefined,
  });

  const visibleRows = table.getRowModel().rows;
  const columnCount = columns.length;

  return (
    <div className="space-y-2">
      <Table.Root
        density={density}
        zebra={zebra}
        bordered={bordered}
        stickyHeader={stickyHeader}
      >
        <Table.Head sticky={stickyHeader}>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const canSort = sortable && header.column.getCanSort();
                return (
                  <Table.HeaderCell
                    key={header.id}
                    isSortable={canSort}
                    sortDirection={header.column.getIsSorted()}
                    onClick={
                      canSort
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </Table.HeaderCell>
                );
              })}
            </tr>
          ))}
        </Table.Head>
        {isLoading ? (
          <DataTableSkeleton columns={columnCount} />
        ) : visibleRows.length === 0 ? (
          <DataTableEmpty columns={columnCount} message={emptyState} />
        ) : (
          <Table.Body>
            {visibleRows.map(row => (
              <Table.Row
                key={row.id}
                isClickable={!!onRowClick}
                onClick={
                  onRowClick ? () => onRowClick(row.original) : undefined
                }
              >
                {row.getVisibleCells().map(cell => (
                  <Table.Cell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        )}
      </Table.Root>
      {pagination && !isLoading && visibleRows.length > 0 && (
        <DataTablePagination
          table={table}
          pageSizeOptions={paginationConfig.pageSizeOptions}
          showSizeChanger={paginationConfig.showSizeChanger}
          showTotal={paginationConfig.showTotal}
          totalCount={totalCount}
        />
      )}
    </div>
  );
};
